import type {
  DifficultyBucket,
  HeatmapDay,
  NormalizedDifficulty,
  RatingHistoryEntry,
  TimeBucketPoint,
  TopicMasteryPoint,
} from '@algo/shared';
import { DIFFICULTY_LABELS, formatDayLabel } from '@algo/shared';
import clsx from 'clsx';
import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

/** Recharts needs literal colours, so read the theme variables at render time. */
function cssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value ? `rgb(${value})` : fallback;
}

function useTheme() {
  return useMemo(
    () => ({
      accent: cssVar('--accent', 'rgb(124 138 255)'),
      success: cssVar('--success', 'rgb(63 185 122)'),
      warning: cssVar('--warning', 'rgb(226 173 74)'),
      danger: cssVar('--danger', 'rgb(232 100 100)'),
      edge: cssVar('--edge', 'rgb(33 41 54)'),
      muted: cssVar('--muted', 'rgb(130 143 160)'),
      panel: cssVar('--panel', 'rgb(17 21 29)'),
      ink: cssVar('--ink', 'rgb(230 237 243)'),
    }),
    [],
  );
}

const axisProps = (colour: string) => ({
  stroke: colour,
  tick: { fill: colour, fontSize: 11 },
  tickLine: false,
  axisLine: false,
});

function tooltipStyle(theme: ReturnType<typeof useTheme>) {
  return {
    contentStyle: {
      background: theme.panel,
      border: `1px solid ${theme.edge}`,
      borderRadius: 8,
      fontSize: 12,
      color: theme.ink,
    },
    labelStyle: { color: theme.muted },
    itemStyle: { color: theme.ink },
  };
}

/* -------------------------- problems solved over time -------------------- */

export function SolvedOverTimeChart({ data }: { data: TimeBucketPoint[] }) {
  const theme = useTheme();
  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
        <defs>
          <linearGradient id="solvedFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.accent} stopOpacity={0.45} />
            <stop offset="100%" stopColor={theme.accent} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.edge} vertical={false} />
        <XAxis dataKey="label" {...axisProps(theme.muted)} />
        <YAxis allowDecimals={false} {...axisProps(theme.muted)} />
        <Tooltip
          {...tooltipStyle(theme)}
          formatter={(value: number) => [`${value}`, 'Шинэ бодлого']}
          labelFormatter={(label: string) => `${label}-ний долоо хоног`}
        />
        <Area
          type="monotone"
          dataKey="problemsSolved"
          stroke={theme.accent}
          strokeWidth={2}
          fill="url(#solvedFill)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/* ------------------------------ rating history --------------------------- */

export function RatingChart({ data }: { data: RatingHistoryEntry[] }) {
  const theme = useTheme();
  const points = data.map((entry) => ({
    date: formatDayLabel(entry.recordedAt.slice(0, 10)),
    rating: entry.rating,
  }));

  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.edge} vertical={false} />
        <XAxis dataKey="date" minTickGap={28} {...axisProps(theme.muted)} />
        <YAxis {...axisProps(theme.muted)} />
        <Tooltip {...tooltipStyle(theme)} formatter={(value: number) => [`${value}`, 'Рэйтинг']} />
        <Line
          type="monotone"
          dataKey="rating"
          stroke={theme.success}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

/* ------------------------------ topic mastery ---------------------------- */

export function TopicMasteryRadar({ data }: { data: TopicMasteryPoint[] }) {
  const theme = useTheme();
  // A 28-spoke radar is unreadable; show the strongest dozen.
  const points = [...data]
    .sort((a, b) => b.mastery - a.mastery)
    .slice(0, 12)
    .map((point) => ({ topic: point.topic, mastery: point.mastery }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <RadarChart data={points} outerRadius="72%">
        <PolarGrid stroke={theme.edge} />
        <PolarAngleAxis dataKey="topic" tick={{ fill: theme.muted, fontSize: 10 }} />
        <PolarRadiusAxis domain={[0, 100]} tick={{ fill: theme.muted, fontSize: 9 }} />
        <Radar
          dataKey="mastery"
          stroke={theme.accent}
          fill={theme.accent}
          fillOpacity={0.28}
        />
        <Tooltip {...tooltipStyle(theme)} formatter={(value: number) => [`${value}/100`, 'Эзэмшилт']} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

export function TopicMasteryBars({ data }: { data: TopicMasteryPoint[] }) {
  const theme = useTheme();
  const points = [...data].sort((a, b) => b.mastery - a.mastery).slice(0, 14);

  return (
    <ResponsiveContainer width="100%" height={Math.max(220, points.length * 26)}>
      <BarChart data={points} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.edge} horizontal={false} />
        <XAxis type="number" domain={[0, 100]} {...axisProps(theme.muted)} />
        <YAxis
          type="category"
          dataKey="topic"
          width={150}
          {...axisProps(theme.muted)}
          tick={{ fill: theme.muted, fontSize: 11 }}
        />
        <Tooltip {...tooltipStyle(theme)} formatter={(value: number) => [`${value}/100`, 'Эзэмшилт']} />
        <Bar dataKey="mastery" radius={[0, 4, 4, 0]} fill={theme.accent} barSize={12} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/* --------------------------- difficulty distribution --------------------- */

export function DifficultyChart({ data }: { data: DifficultyBucket[] }) {
  const theme = useTheme();
  const colours: Record<string, string> = {
    EASY: theme.success,
    MEDIUM: theme.accent,
    HARD: theme.warning,
    EXPERT: theme.danger,
  };

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.edge} vertical={false} />
        <XAxis
          dataKey="difficulty"
          tickFormatter={(value: NormalizedDifficulty) => DIFFICULTY_LABELS[value] ?? value}
          {...axisProps(theme.muted)}
        />
        <YAxis allowDecimals={false} {...axisProps(theme.muted)} />
        <Tooltip {...tooltipStyle(theme)} formatter={(value: number) => [`${value}`, 'Бодлого']} />
        <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={44}>
          {data.map((bucket) => (
            <Cell key={bucket.difficulty} fill={colours[bucket.difficulty] ?? theme.accent} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ------------------------------ growth (monthly) ------------------------- */

export function GrowthChart({ data }: { data: { day: string; rating: number }[] }) {
  const theme = useTheme();
  const points = data.map((point) => ({ label: formatDayLabel(point.day), rating: point.rating }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
        <defs>
          <linearGradient id="growthFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.success} stopOpacity={0.4} />
            <stop offset="100%" stopColor={theme.success} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.edge} vertical={false} />
        <XAxis dataKey="label" minTickGap={24} {...axisProps(theme.muted)} />
        <YAxis domain={['dataMin - 50', 'dataMax + 50']} {...axisProps(theme.muted)} />
        <Tooltip {...tooltipStyle(theme)} formatter={(value: number) => [`${value}`, 'Рэйтинг']} />
        <Area
          type="monotone"
          dataKey="rating"
          stroke={theme.success}
          strokeWidth={2}
          fill="url(#growthFill)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/* -------------------------- consistency calendar ------------------------- */

const LEVEL_CLASSES = [
  'bg-raised',
  'bg-accent/25',
  'bg-accent/45',
  'bg-accent/70',
  'bg-accent',
] as const;

/**
 * GitHub-style contribution calendar. Columns are ISO weeks, rows are weekdays;
 * a day is filled when it counted as active.
 */
export function ConsistencyCalendar({ days }: { days: HeatmapDay[] }) {
  const weeks = useMemo(() => {
    if (days.length === 0) return [] as HeatmapDay[][];
    const columns: HeatmapDay[][] = [];
    let current: HeatmapDay[] = [];

    const weekdayOf = (day: string): number => {
      const dow = new Date(`${day}T00:00:00Z`).getUTCDay();
      return dow === 0 ? 6 : dow - 1; // Monday = 0
    };

    // Pad the first column so the grid starts on the right weekday.
    const firstDay = days[0];
    if (firstDay) {
      for (let i = 0; i < weekdayOf(firstDay.day); i += 1) {
        current.push({ day: '', problemsSolved: 0, minutes: 0, active: false, level: -1 });
      }
    }

    for (const day of days) {
      current.push(day);
      if (current.length === 7) {
        columns.push(current);
        current = [];
      }
    }
    if (current.length > 0) columns.push(current);
    return columns;
  }, [days]);

  return (
    <div className="overflow-x-auto pb-1">
      <div className="flex gap-[3px]">
        {weeks.map((week, index) => (
          <div key={index} className="flex flex-col gap-[3px]">
            {week.map((day, dayIndex) => (
              <div
                key={day.day || `pad-${dayIndex}`}
                title={
                  day.level < 0
                    ? undefined
                    : `${day.day} — ${day.problemsSolved} бодолт, ${day.minutes} мин`
                }
                className={clsx(
                  'h-[11px] w-[11px] rounded-sm',
                  day.level < 0 ? 'bg-transparent' : LEVEL_CLASSES[day.level],
                )}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs text-muted">
        <span>Бага</span>
        {LEVEL_CLASSES.map((cls, index) => (
          <span key={index} className={clsx('h-[11px] w-[11px] rounded-sm', cls)} />
        ))}
        <span>Их</span>
      </div>
    </div>
  );
}
